import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';

import { Router } from '@angular/router';
import { AuthService } from '../../../../services/auth.service';
import { StripeService, StripeProduct } from '../../../../services/stripe.service';
import { take } from 'rxjs/operators';

interface PricingPlan {
  name: string;
  description: string;
  price: string;
  period?: string;
  badge?: string;
  popular?: boolean;
  comingSoon?: boolean;
  features: string[];
  cta: string;
  note?: string;
  productName?: string; // Maps to Stripe product name
}

@Component({
  selector: 'app-pricing',
  standalone: true,
  imports: [],
  templateUrl: './pricing-v2.component.html'
})
export class PricingComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);
  private stripeService = inject(StripeService);
  private intersectionObserver?: IntersectionObserver;

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  plans: PricingPlan[] = [
    {
      name: 'Standard',
      description: 'For individual advisors looking to retain AUM through credit solutions',
      price: '$25',
      period: '/mo',
      badge: 'First 30 days free',
      features: [
        'Unlimited single user term requests',
        'Full access to ALL credit solutions: Residential and Commercial Real Estate, Business Lending, SBLOC, iBLOC and Luxury Assets',
        'Competitive credit terms from vetted lenders',
        'Interactive dashboard with live pipeline and term visibility',
        'Acquisition loans: buy / sell / refi a book of business'
      ],
      cta: 'Start Free Trial',
      productName: 'Standard' // Maps to Stripe product
    },
    {
      name: 'Professional',
      description: 'For individual or team advisors looking to retain AUM and enhance credit services',
      price: '$199',
      period: '/mo',
      popular: true,
      comingSoon: true,
      features: [
        'Everything in Standard',
        'Three additional full access team seats',
        '$99 account credit after 2 funded deals/month*',
        'Automated draw requests on SBLOCs, iBLOCs, HELOCs...',
        'Live view of funded client activity',
        'Real-time tracking + client interaction',
        'Monthly reporting on assets preserved and client impact'
      ],
      note: '*Credit is issued to your account and applied to the next billing cycle after two funded transactions',
      cta: 'Get Professional'
    },
    {
      name: 'Enterprise',
      description: 'For firms and advisor networks operating at scale',
      price: 'Contact Us',
      features: [
        'Everything in Professional',
        'Scaled pricing for multi-user teams',
        'Revenue options on eligible credit solutions',
        'Grey-labeled platform',
        'Dedicated acscount manager and onboarding support',
        'Organization-wide reporting: usage insights, advisor performance, and asset tracking'
      ],
      cta: 'Contact Sales'
    }
  ];

  ngOnInit() {
    this.setupIntersectionObserver();
  }

  ngOnDestroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  private setupIntersectionObserver() {
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in-view');
              this.intersectionObserver?.unobserve(entry.target);
            }
          });
        },
        { root: null, rootMargin: '0px', threshold: 0.1 }
      );

      setTimeout(() => {
        document.querySelectorAll('#pricing .observe-viewport').forEach((el) => {
          this.intersectionObserver?.observe(el);
        });
      }, 100);
    }
  }

  handlePlanAction(plan: PricingPlan) {
    if (plan.name === 'Enterprise') {
      this.scrollToContact();
      return;
    }

    // For Standard plan (and future Professional), initiate Stripe checkout
    this.authService.currentUser$.pipe(take(1)).subscribe(async user => {
      if (!user) {
        // User needs to log in first - redirect to auth with return URL
        this.router.navigate(['/auth'], { 
          queryParams: { returnUrl: '/', fragment: 'pricing' } 
        });
        return;
      }

      // Start checkout process
      this.isLoading.set(true);
      this.errorMessage.set(null);

      try {
        // Find the product by name
        const productName = plan.productName || plan.name;
        
        const product = await this.findProductByName(productName);
        
        if (!product) {
          throw new Error(`Product "${productName}" not found. Please check that a product with this name exists in Stripe.`);
        }

        // Get the monthly price
        const monthlyPrice = this.stripeService.getPriceByInterval(product, 'month');
        
        if (!monthlyPrice) {
          throw new Error('No monthly price found for this product. Please check your Stripe product configuration.');
        }

        // Create checkout session with 30-day trial
        const checkoutSession$ = await this.stripeService.createCheckoutSession(
          monthlyPrice.id,
          `${window.location.origin}/advisor-portal?subscription=success`,
          `${window.location.origin}/#pricing`,
          user,
          30 // 30-day free trial
        );

        // Subscribe to get the checkout URL
        checkoutSession$.subscribe({
          next: (session) => {
            if (session.url) {
              // Redirect to Stripe Checkout
              this.stripeService.redirectToCheckout(session.url);
            }
          },
          error: (error) => {
            console.error('Error creating checkout session:', error);
            this.errorMessage.set('Failed to start checkout. Please try again.');
            this.isLoading.set(false);
          }
        });

      } catch (error: any) {
        console.error('Error in checkout process:', error);
        this.errorMessage.set(error.message || 'Failed to start checkout. Please try again.');
        this.isLoading.set(false);
      }
    });
  }

  private findProductByName(name: string): Promise<StripeProduct | undefined> {
    return new Promise((resolve) => {
      // Wait for products to load if they haven't yet
      const checkProducts = (attempts: number) => {
        this.stripeService.products$.pipe(take(1)).subscribe(products => {
          const product = products.find(p => 
            p.name.toLowerCase().includes(name.toLowerCase())
          );
          
          if (product) {
            resolve(product);
          } else if (products.length === 0 && attempts < 10) {
            // Products not loaded yet, wait and retry
            setTimeout(() => checkProducts(attempts + 1), 500);
          } else {
            resolve(undefined);
          }
        });
      };
      
      checkProducts(0);
    });
  }

  private scrollToContact() {
    const element = document.getElementById('contact');
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

}

